import * as chatApi from "@lib/chatApi";
import { useState } from "react";

interface PlanModeToggleProps {
  /** Omit before the thread exists (composer view) — toggle becomes pure local state. */
  threadId?: string;
  enabled: boolean;
  onChange: (next: { enabled: boolean }) => void;
}

export function PlanModeToggle({ threadId, enabled, onChange }: PlanModeToggleProps) {
  const [busy, setBusy] = useState(false);

  async function toggle() {
    if (busy) return;
    const next = !enabled;
    if (!threadId) {
      onChange({ enabled: next });
      return;
    }
    setBusy(true);
    try {
      const thread = await chatApi.updatePlanMode(threadId, next);
      onChange({ enabled: thread.plan_mode_enabled ?? next });
    } finally {
      setBusy(false);
    }
  }

  const tooltip = enabled
    ? "Plan mode is on — the assistant must propose a plan and wait for your approval before any write. Click to turn off."
    : "Plan mode is off — the assistant runs write tools directly. Click to require plan approval first.";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={tooltip}
      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors ${
        enabled
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-text-tertiary/30 text-text-tertiary hover:border-text-tertiary/50"
      } disabled:opacity-60`}
    >
      <span
        className={`inline-block h-3 w-5 rounded-full transition-colors ${
          enabled ? "bg-accent" : "bg-text-tertiary/30"
        }`}
      >
        <span
          className={`block h-3 w-3 rounded-full bg-white transition-transform ${
            enabled ? "translate-x-2" : "translate-x-0"
          }`}
        />
      </span>
      Plan mode
    </button>
  );
}
